<script setup lang="ts">
import { ref } from 'vue'

/**
 * Inline rename field — the emerald-ringed text input shown in place of a row title while editing
 * (tour / waypoint / POI / collection names). v-model'd; commits on Enter and blur, cancels on Esc;
 * stops click propagation so the row underneath doesn't react. Exposes `inputEl` so the caller can
 * focus/select it when edit mode opens.
 */
interface Props {
  modelValue: string
  placeholder?: string
  size?: 'sm' | 'base'
}
const props = withDefaults(defineProps<Props>(), { size: 'sm' })
const emit = defineEmits<{
  'update:modelValue': [value: string]
  commit: []
  cancel: []
}>()

const inputEl = ref<HTMLInputElement | null>(null)
defineExpose({ inputEl })
</script>

<template>
  <input
    ref="inputEl"
    type="text"
    :value="props.modelValue"
    :placeholder="props.placeholder"
    class="w-full rounded border border-emerald-400 px-1.5 py-0.5 font-medium focus:outline-none focus:ring-1 focus:ring-emerald-500"
    :class="props.size === 'base' ? 'text-base' : 'text-sm'"
    @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
    @click.stop
    @keydown.enter.prevent="emit('commit')"
    @keydown.esc.prevent="emit('cancel')"
    @blur="emit('commit')"
  />
</template>
